import { useState } from 'react'
import { useUMLAutoSave } from '@/hooks/useUMLAutoSave'
import { useInterviewStore } from '@/store/interviewStore'
import { 
  Layers, Square, Circle, Triangle, Users, ArrowRight,
  Plus, Trash2, Edit, Save, X, RotateCcw, Download, Link2
} from 'lucide-react'

interface UMLClass {
  id: string
  name: string
  type: 'class' | 'interface' | 'abstract' | 'actor' | 'usecase'
  x: number
  y: number
  attributes: ClassAttribute[]
  methods: ClassMethod[]
}

interface ClassAttribute {
  id: string
  name: string
  type: string
  visibility: 'public' | 'private' | 'protected'
}

interface ClassMethod {
  id: string
  name: string
  returnType: string
  parameters: string
  visibility: 'public' | 'private' | 'protected'
}

interface UMLRelationship {
  id: string
  fromClassId: string
  toClassId: string
  type: 'inheritance' | 'composition' | 'aggregation' | 'association' | 'dependency'
  label: string
}

const UML_SHAPES = [
  { id: 'class', name: 'Class', icon: Square, color: 'bg-blue-500' },
  { id: 'interface', name: 'Interface', icon: Circle, color: 'bg-green-500' },
  { id: 'abstract', name: 'Abstract Class', icon: Triangle, color: 'bg-purple-500' },
  { id: 'actor', name: 'Actor', icon: Users, color: 'bg-orange-500' },
  { id: 'usecase', name: 'Use Case', icon: Circle, color: 'bg-teal-500' }
]

export default function UMLDesign() {
  const store = useInterviewStore()
  const umlData = store.getUMLData()
  
  // Use store data directly instead of local state
  const [selectedClass, setSelectedClass] = useState<string | null>(null)
  const [showClassModal, setShowClassModal] = useState<string | null>(null)
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null)
  const [selectedShape, setSelectedShape] = useState<string>('class')
  const [isDragOver, setIsDragOver] = useState(false)

  const { isDirty, lastSaved } = useUMLAutoSave({ 
    intervalMs: 5000,
    classes: umlData.classes,
    relationships: umlData.relationships
  })

  const handleContentChange = (newContent: string) => {
    store.updateUMLContent(newContent)
  }

  const addClass = (shapeType: string = selectedShape, x?: number, y?: number) => {
    const newClass: UMLClass = {
      id: `class-${Date.now()}`,
      name: `New${shapeType.charAt(0).toUpperCase() + shapeType.slice(1)}`,
      type: shapeType as any,
      x: x ?? Math.random() * 400 + 100,
      y: y ?? Math.random() * 300 + 100,
      attributes: [],
      methods: []
    }
    store.addUMLClass(newClass)
    setShowClassModal(newClass.id)
  }

  const updateClass = (classId: string, updates: Partial<UMLClass>) => {
    store.updateUMLClass(classId, updates)
  }

  const deleteClass = (classId: string) => {
    store.removeUMLClass(classId)
  }

  const addAttribute = (classId: string) => {
    const newAttribute: ClassAttribute = {
      id: `attr-${Date.now()}`,
      name: 'newAttribute',
      type: 'String',
      visibility: 'private'
    }
    
    const cls = umlData.classes.find(c => c.id === classId)
    if (cls) {
      updateClass(classId, {
        attributes: [...cls.attributes, newAttribute]
      })
    }
  }

  const addMethod = (classId: string) => {
    const newMethod: ClassMethod = {
      id: `method-${Date.now()}`,
      name: 'newMethod',
      returnType: 'void',
      parameters: '',
      visibility: 'public'
    }
    
    const cls = umlData.classes.find(c => c.id === classId)
    if (cls) {
      updateClass(classId, {
        methods: [...cls.methods, newMethod]
      })
    }
  }

  const createRelationship = (fromId: string, toId: string) => {
    const newRelationship: UMLRelationship = {
      id: `rel-${Date.now()}`,
      fromClassId: fromId,
      toClassId: toId,
      type: 'association',
      label: ''
    }
    store.addUMLRelationship(newRelationship)
  }

  const handleClassClick = (classId: string) => {
    if (connectingFrom) {
      if (connectingFrom !== classId) {
        createRelationship(connectingFrom, classId)
      }
      setConnectingFrom(null)
    } else {
      setSelectedClass(classId)
    }
  }

  const getVisibilitySymbol = (visibility: string) => {
    switch (visibility) {
      case 'public': return '+'
      case 'private': return '-'
      case 'protected': return '#'
      default: return ''
    }
  }

  const getSaveStatus = () => {
    if (isDirty) {
      return <span className="text-amber-600">● Unsaved changes</span>
    } else if (lastSaved) {
      return <span className="text-green-600">✓ Saved {new Date(lastSaved).toLocaleTimeString()}</span>
    }
    return <span className="text-gray-500">No changes</span>
  }

  const handleDragStart = (e: React.DragEvent, shapeType: string) => {
    e.dataTransfer.setData('application/uml-shape', shapeType)
    e.dataTransfer.effectAllowed = 'copy'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const shapeType = e.dataTransfer.getData('application/uml-shape')
    if (shapeType) {
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left - 100 // Center the shape
      const y = e.clientY - rect.top - 50
      addClass(shapeType, Math.max(0, x), Math.max(0, y))
    }
  }

  const exportDiagram = () => {
    const diagram = {
      classes: umlData.classes.map(cls => ({
        name: cls.name,
        type: cls.type,
        attributes: cls.attributes.map(attr => ({
          name: attr.name,
          type: attr.type,
          visibility: attr.visibility
        })),
        methods: cls.methods.map(method => ({
          name: method.name,
          returnType: method.returnType,
          parameters: method.parameters,
          visibility: method.visibility
        }))
      })),
      relationships: umlData.relationships.map(rel => ({
        from: umlData.classes.find(c => c.id === rel.fromClassId)?.name,
        to: umlData.classes.find(c => c.id === rel.toClassId)?.name,
        type: rel.type,
        label: rel.label
      })),
      timestamp: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(diagram, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'uml-diagram.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Layers className="w-6 h-6 text-purple-600" />
            <h2 className="text-lg font-semibold">UML Design</h2>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="text-sm">
              {getSaveStatus()}
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => addClass()}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add {UML_SHAPES.find(s => s.id === selectedShape)?.name}</span>
              </button>
              
              <button
                onClick={() => {
                  store.updateUMLClasses([])
                  store.updateUMLRelationships([])
                  setSelectedClass(null)
                  setConnectingFrom(null)
                }}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Clear</span>
              </button>
              
              <button
                onClick={exportDiagram}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="text-sm text-gray-600">
          Create UML class diagrams, use case diagrams, and component relationships. Select shapes, add classes, and connect them with relationships.
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex">
        {/* Left Panel - Shapes and Tools */}
        <div className="w-1/4 p-4 border-r border-gray-200 overflow-y-auto">
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2 text-gray-900">UML Shapes</h3>
              <div className="space-y-2">
                {UML_SHAPES.map((shape) => {
                  const Icon = shape.icon
                  return (
                    <button
                      key={shape.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, shape.id)}
                      onClick={() => setSelectedShape(shape.id)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors cursor-grab active:cursor-grabbing ${
                        selectedShape === shape.id
                          ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                          : `${shape.color} text-white hover:opacity-90`
                      }`}
                      title={`Drag to canvas or click to select`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{shape.name}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2 text-gray-900">Tools</h3>
              <button
                onClick={() => setConnectingFrom(connectingFrom ? null : 'connect')}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                  connectingFrom ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Link2 className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {connectingFrom ? 'Cancel Connect' : 'Connect Classes'}
                </span>
              </button>
              {connectingFrom && (
                <div className="mt-2 p-2 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200">
                  Click on two classes to create a relationship
                </div>
              )}
            </div>

            <div>
              <h3 className="font-semibold mb-2 text-gray-900">Relationships</h3>
              <div className="space-y-2 text-xs">
                <div>→ Association</div>
                <div>⬥→ Composition</div>
                <div>◇→ Aggregation</div>
                <div>△→ Inheritance</div>
                <div>⋯→ Dependency</div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-2 text-gray-900">Instructions</h3>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Drag shapes from palette to canvas</li>
                <li>• Or select shape and click "Add"</li>
                <li>• Click classes to edit details</li>
                <li>• Use "Connect Classes" for relationships</li>
                <li>• Drag classes to reposition</li>
                <li>• Export diagram when complete</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Main Canvas */}
        <div className="flex-1 p-4">
          <div 
            className={`h-full border-2 border-dashed rounded-lg relative overflow-hidden transition-colors ${
              isDragOver 
                ? 'bg-purple-50 border-purple-400 border-solid' 
                : 'bg-gray-50 border-gray-300'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {umlData.classes.length === 0 && !isDragOver && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Layers className="w-12 h-12 mx-auto mb-2" />
                  <p className="text-sm">Drag UML shapes here or click "Add" to start designing</p>
                </div>
              </div>
            )}
            
            {isDragOver && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-purple-600">
                  <Layers className="w-16 h-16 mx-auto mb-4 animate-pulse" />
                  <p className="text-lg font-semibold">Drop shape here to add to diagram</p>
                </div>
              </div>
            )}

            {/* Render Classes */}
            {umlData.classes.map((cls) => {
              const shape = UML_SHAPES.find(s => s.id === cls.type)
              const Icon = shape?.icon || Square
              
              return (
                <div
                  key={cls.id}
                  onClick={() => handleClassClick(cls.id)}
                  onMouseDown={(e) => {
                    if (connectingFrom) return
                    const startX = e.clientX - cls.x
                    const startY = e.clientY - cls.y
                    
                    const handleMouseMove = (e: MouseEvent) => {
                      updateClass(cls.id, {
                        x: e.clientX - startX,
                        y: e.clientY - startY
                      })
                    }
                    
                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove)
                      document.removeEventListener('mouseup', handleMouseUp)
                    }
                    
                    document.addEventListener('mousemove', handleMouseMove)
                    document.addEventListener('mouseup', handleMouseUp)
                  }}
                  className={`absolute bg-white border-2 rounded-lg shadow-lg cursor-pointer transition-all ${
                    selectedClass === cls.id ? 'border-purple-500 shadow-xl' : 'border-gray-300 hover:border-gray-400'
                  } ${connectingFrom ? 'hover:border-blue-400' : 'hover:shadow-xl'}`}
                  style={{
                    left: cls.x,
                    top: cls.y,
                    minWidth: cls.type === 'actor' || cls.type === 'usecase' ? '120px' : '200px',
                    maxWidth: '280px'
                  }}
                >
                  {cls.type === 'actor' ? (
                    // Actor representation
                    <div className="p-4 text-center">
                      <Users className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                      <div className="font-semibold text-sm">{cls.name}</div>
                    </div>
                  ) : cls.type === 'usecase' ? (
                    // Use case representation
                    <div className="p-4 text-center">
                      <div className="border-2 border-teal-500 rounded-full p-4 inline-block">
                        <div className="font-semibold text-sm text-teal-700">{cls.name}</div>
                      </div>
                    </div>
                  ) : (
                    // Class/Interface representation
                    <>
                      {/* Class Header */}
                      <div className={`px-3 py-2 rounded-t-lg border-b ${
                        cls.type === 'interface' ? 'bg-green-100' : 
                        cls.type === 'abstract' ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Icon className={`w-4 h-4 ${
                              cls.type === 'interface' ? 'text-green-600' : 
                              cls.type === 'abstract' ? 'text-purple-600' : 'text-blue-600'
                            }`} />
                            <div>
                              {cls.type === 'interface' && <div className="text-xs text-gray-500">«interface»</div>}
                              {cls.type === 'abstract' && <div className="text-xs text-gray-500">«abstract»</div>}
                              <span className={`font-semibold text-sm ${
                                cls.type === 'interface' ? 'text-green-800 italic' : 
                                cls.type === 'abstract' ? 'text-purple-800 italic' : 'text-blue-800'
                              }`}>{cls.name}</span>
                            </div>
                          </div>
                          <div className="flex space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setShowClassModal(cls.id)
                              }}
                              className="p-1 text-gray-600 hover:bg-gray-200 rounded"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                deleteClass(cls.id)
                              }}
                              className="p-1 text-red-600 hover:bg-red-200 rounded"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Attributes */}
                      <div className="px-3 py-2 border-b">
                        {cls.attributes.length === 0 ? (
                          <div className="text-xs text-gray-500 text-center">No attributes</div>
                        ) : (
                          <div className="space-y-1">
                            {cls.attributes.slice(0, 3).map((attr) => (
                              <div key={attr.id} className="text-xs font-mono">
                                {getVisibilitySymbol(attr.visibility)} {attr.name}: {attr.type}
                              </div>
                            ))}
                            {cls.attributes.length > 3 && (
                              <div className="text-xs text-gray-500">+{cls.attributes.length - 3} more...</div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Methods */}
                      <div className="px-3 py-2">
                        {cls.methods.length === 0 ? (
                          <div className="text-xs text-gray-500 text-center">No methods</div>
                        ) : (
                          <div className="space-y-1">
                            {cls.methods.slice(0, 3).map((method) => (
                              <div key={method.id} className="text-xs font-mono">
                                {getVisibilitySymbol(method.visibility)} {method.name}({method.parameters}): {method.returnType}
                              </div>
                            ))}
                            {cls.methods.length > 3 && (
                              <div className="text-xs text-gray-500">+{cls.methods.length - 3} more...</div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )
            })}

            {/* Relationship lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              {umlData.relationships.map((rel) => {
                const fromClass = umlData.classes.find(c => c.id === rel.fromClassId)
                const toClass = umlData.classes.find(c => c.id === rel.toClassId)
                if (!fromClass || !toClass) return null

                const fromX = fromClass.x + 100
                const fromY = fromClass.y + 50
                const toX = toClass.x + 100
                const toY = toClass.y + 50

                return (
                  <g key={rel.id}>
                    <line
                      x1={fromX}
                      y1={fromY}
                      x2={toX}
                      y2={toY}
                      stroke="#374151"
                      strokeWidth="2"
                      strokeDasharray={rel.type === 'dependency' ? '5,5' : 'none'}
                      markerEnd={`url(#${rel.type})`}
                    />
                    {rel.label && (
                      <text
                        x={(fromX + toX) / 2}
                        y={(fromY + toY) / 2 - 5}
                        fontSize="12"
                        textAnchor="middle"
                        fill="#374151"
                      >
                        {rel.label}
                      </text>
                    )}
                  </g>
                )
              })}
              <defs>
                <marker id="association" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#374151" />
                </marker>
                <marker id="inheritance" markerWidth="12" markerHeight="10" refX="11" refY="5" orient="auto">
                  <polygon points="0 0, 12 5, 0 10" fill="none" stroke="#374151" strokeWidth="2" />
                </marker>
                <marker id="composition" markerWidth="12" markerHeight="8" refX="11" refY="4" orient="auto">
                  <polygon points="0 4, 6 0, 12 4, 6 8" fill="#374151" />
                </marker>
                <marker id="aggregation" markerWidth="12" markerHeight="8" refX="11" refY="4" orient="auto">
                  <polygon points="0 4, 6 0, 12 4, 6 8" fill="white" stroke="#374151" strokeWidth="1" />
                </marker>
                <marker id="dependency" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#374151" />
                </marker>
              </defs>
            </svg>
          </div>
        </div>
      </div>

      {/* Class Modal */}
      {showClassModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit {umlData.classes.find(c => c.id === showClassModal)?.type}</h3>
              <button
                onClick={() => setShowClassModal(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {(() => {
              const cls = umlData.classes.find(c => c.id === showClassModal)
              if (!cls) return null

              return (
                <div className="space-y-6">
                  {/* Class Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {cls.type === 'actor' ? 'Actor' : cls.type === 'usecase' ? 'Use Case' : 'Class'} Name
                    </label>
                    <input
                      type="text"
                      value={cls.name}
                      onChange={(e) => updateClass(cls.id, { name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {cls.type !== 'actor' && cls.type !== 'usecase' && (
                    <>
                      {/* Attributes */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">Attributes</label>
                          <button
                            onClick={() => addAttribute(cls.id)}
                            className="flex items-center space-x-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            <Plus className="w-4 h-4" />
                            <span>Add Attribute</span>
                          </button>
                        </div>

                        <div className="space-y-2">
                          {cls.attributes.map((attr) => (
                            <div key={attr.id} className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-50 rounded">
                              <select
                                value={attr.visibility}
                                onChange={(e) => {
                                  const updatedAttrs = cls.attributes.map(a => 
                                    a.id === attr.id ? { ...a, visibility: e.target.value as any } : a
                                  )
                                  updateClass(cls.id, { attributes: updatedAttrs })
                                }}
                                className="col-span-2 px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="public">Public</option>
                                <option value="private">Private</option>
                                <option value="protected">Protected</option>
                              </select>
                              <input
                                type="text"
                                value={attr.name}
                                onChange={(e) => {
                                  const updatedAttrs = cls.attributes.map(a => 
                                    a.id === attr.id ? { ...a, name: e.target.value } : a
                                  )
                                  updateClass(cls.id, { attributes: updatedAttrs })
                                }}
                                className="col-span-4 px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Attribute name"
                              />
                              <input
                                type="text"
                                value={attr.type}
                                onChange={(e) => {
                                  const updatedAttrs = cls.attributes.map(a => 
                                    a.id === attr.id ? { ...a, type: e.target.value } : a
                                  )
                                  updateClass(cls.id, { attributes: updatedAttrs })
                                }}
                                className="col-span-4 px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Type"
                              />
                              <button
                                onClick={() => {
                                  const updatedAttrs = cls.attributes.filter(a => a.id !== attr.id)
                                  updateClass(cls.id, { attributes: updatedAttrs })
                                }}
                                className="col-span-2 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Methods */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">Methods</label>
                          <button
                            onClick={() => addMethod(cls.id)}
                            className="flex items-center space-x-1 px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            <Plus className="w-4 h-4" />
                            <span>Add Method</span>
                          </button>
                        </div>

                        <div className="space-y-2">
                          {cls.methods.map((method) => (
                            <div key={method.id} className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-50 rounded">
                              <select
                                value={method.visibility}
                                onChange={(e) => {
                                  const updatedMethods = cls.methods.map(m => 
                                    m.id === method.id ? { ...m, visibility: e.target.value as any } : m
                                  )
                                  updateClass(cls.id, { methods: updatedMethods })
                                }}
                                className="col-span-2 px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="public">Public</option>
                                <option value="private">Private</option>
                                <option value="protected">Protected</option>
                              </select>
                              <input
                                type="text"
                                value={method.name}
                                onChange={(e) => {
                                  const updatedMethods = cls.methods.map(m => 
                                    m.id === method.id ? { ...m, name: e.target.value } : m
                                  )
                                  updateClass(cls.id, { methods: updatedMethods })
                                }}
                                className="col-span-3 px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Method name"
                              />
                              <input
                                type="text"
                                value={method.parameters}
                                onChange={(e) => {
                                  const updatedMethods = cls.methods.map(m => 
                                    m.id === method.id ? { ...m, parameters: e.target.value } : m
                                  )
                                  updateClass(cls.id, { methods: updatedMethods })
                                }}
                                className="col-span-3 px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Parameters"
                              />
                              <input
                                type="text"
                                value={method.returnType}
                                onChange={(e) => {
                                  const updatedMethods = cls.methods.map(m => 
                                    m.id === method.id ? { ...m, returnType: e.target.value } : m
                                  )
                                  updateClass(cls.id, { methods: updatedMethods })
                                }}
                                className="col-span-2 px-2 py-1 border border-gray-300 rounded text-sm"
                                placeholder="Return type"
                              />
                              <button
                                onClick={() => {
                                  const updatedMethods = cls.methods.filter(m => m.id !== method.id)
                                  updateClass(cls.id, { methods: updatedMethods })
                                }}
                                className="col-span-2 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Save Button */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => setShowClassModal(null)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}